<script lang="ts">
  import FizzStyles from "@shared/svelte/Components/FizzStyles.svelte";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import PhoneInput from "@shared/svelte/Components/PhoneInput.svelte";
  import Flash from "@shared/services/flashv2.js";
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
  import ImageUploadWidget from "@shared/svelte/Components/ImageUploadWidget.svelte";
  import type { ImageObj, Dimensions } from "@shared/types/Images";
  import { DistributorStore } from "@distributor/svelte/Stores/DistributorStore";
  import type { DistributorUser } from "@shared/types/Distributor";

  let user: any = $CurrentUserStore;
  let currentDistributor: DistributorUser | null = $DistributorStore.currentDistributor;
  let firstName = user.first_name;
  let lastName = user.last_name;
  let email = user.email;
  let phone = user.phone;
  let password = "";
  let confirmPassword = "";
  interface FormData {
    user: {
      email: string;
      first_name: string;
      last_name: string;
      phone: string;
      password?: string;
      password_confirmation?: string;
    };
  }

  async function submitPasswordForm() {
    try {
      if (password && confirmPassword) {
        const data = {
          user: {
            password: password,
            password_confirmation: confirmPassword,
          },
        };
        await HttpPromise({ method: "PUT", url: "/api/distributor/user/password", data });
        const passwordMsg = "We have updated your password";
        Flash.now({ type: "success", message: passwordMsg });
        password = "";
        confirmPassword = "";
      }
    } catch (error) {
      const passwordErrorMsg =
        "There was an error when updating your password. Please, contact support!";
      const message = error?.errors?.messages?.join(", ") || passwordErrorMsg;
      Flash.now({ type: "error", message });
    }
  }

  async function submitForm() {
    try {
      const data = {
        user: {
          email,
          first_name: firstName,
          last_name: lastName,
          phone,
        },
      };

      await HttpPromise({ method: "PUT", url: "/api/distributor/user", data });
      const genericMsg = "We have updated your information";
      Flash.now({ type: "success", message: genericMsg });

      CurrentUserStore.update(u => {
        return { ...u, email, first_name: firstName, last_name: lastName, phone };
      });
    } catch (error) {
      const genericErrorMsg =
        "There was an error when updating your profile. Please, contact support!";
      const message = error?.[0] || genericErrorMsg;
      Flash.now({ type: "error", message });
    }
  }

  let imageObj: ImageObj = {
    id: currentDistributor?.distributor_id,
    image_upload_id: currentDistributor?.image_upload_id,
    cloudinary_image_url: currentDistributor?.cloudinary_image_url,
  };
  let distributorOrPaymentsAdmin = true;

  const dimensions: Dimensions = {
    min_width: 150,
    min_height: 150,
    max_file_size: 5,
  };

  function afterSaveImage() {
    Flash.now({ type: "success", message: "image uploaded" });
  }

  $: {
    distributorOrPaymentsAdmin =
      currentDistributor &&
      (currentDistributor.role === "distributor_admin" ||
        currentDistributor.role === "payments_admin");
  }
</script>

<FizzStyles>
  <div class="fizz-surface">
    <div class="fizz-container fizz-inset-squish-16 fizz-stack-24">
      <h3 class="fizz-heading-4">Profile</h3>
    </div>
  </div>
  <form class="fizz-container fizz-stack-24" on:submit|preventDefault={submitForm}>
    <fieldset class="fizz-flex-row-responsive fizz-surface fizz-inset-square-12 fizz-space-between">
      <label class="fizz-stack-4 fizz-full-width">
        <p class="fizz-label fizz-stack-4">First Name</p>
        <input id="first_name" class="fizz-input-text fizz-full-width" bind:value={firstName} />
      </label>
      <label class="fizz-stack-4 fizz-full-width">
        <p class="fizz-label fizz-stack-4">Last Name</p>
        <input id="last_name" class="fizz-input-text fizz-full-width" bind:value={lastName} />
      </label>
    </fieldset>
    <fieldset class="fizz-flex-row-responsive fizz-surface fizz-inset-square-12 fizz-space-between">
      <label class="fizz-stack-4 fizz-full-width">
        <p class="fizz-label fizz-stack-4">Email Address</p>
        <input id="email" class="fizz-input-text fizz-full-width" type="email" bind:value={email} />
      </label>
      <div class="fizz-full-width">
        <PhoneInput
          bind:value={phone}
          id="phone"
          label="Phone"
          extraClasses="fizz-input-text fizz-stack-4"
        />
      </div>
    </fieldset>
    <fieldset
      class="fizz-flex-row-responsive fizz-surface fizz-justify-end fizz-inset-square-12 fizz-stack-24"
    >
      <button class="fizz-button-primary submit-profile">Save</button>
    </fieldset>
  </form>

  <form class="fizz-container fizz-stack-24" on:submit|preventDefault={submitPasswordForm}>
    <fieldset class="fizz-flex-row-responsive fizz-surface fizz-inset-square-12 fizz-space-between">
      <label class="fizz-stack-4 fizz-full-width">
        <p class="fizz-label fizz-stack-4">New Password</p>
        <input
          id="password"
          class="fizz-input-text fizz-full-width"
          type="password"
          bind:value={password}
        />
      </label>
      <label class="fizz-stack-4 fizz-full-width">
        <p class="fizz-label fizz-stack-4">Confirm New Password</p>
        <input
          id="confirm-password"
          class="fizz-input-text fizz-full-width"
          type="password"
          bind:value={confirmPassword}
        />
      </label>
    </fieldset>
    <fieldset class="fizz-flex-row-responsive fizz-surface fizz-justify-end fizz-inset-square-12">
      <button class="fizz-button-primary submit-password">Change Password</button>
    </fieldset>
  </form>
  {#if distributorOrPaymentsAdmin}
    <section class="fizz-container fizz-stack-24">
      <div class="fizz-inset-squish-64 background-white">
        <h4 class="fizz-heading-4 fizz-stack-4">Product Sheet Logo</h4>
        <p class="fizz-stack-16 fizz-text-subdued">SVG or High Resolution JPEG Files Accepted</p>
        <ImageUploadWidget
          obj={imageObj}
          entityType="Distributor"
          postSaveCB={afterSaveImage}
          {dimensions}
        />
      </div>
    </section>
  {/if}
</FizzStyles>
